import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { IMaskInput } from "react-imask";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Mail, User, Smartphone, IdCard } from "lucide-react";
import { Spin } from "antd";
import axios from "axios";
import Globais, { logout, validateCelular, validateCPF, validateToken } from "../../components/Globais";
import { numberPadrao } from "../../uteis/formatPhoneNumber";
import { useMessage } from "../../uteis/mensagens/useMessage";

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }

  .phone-input {
    width: 100% !important;
    height: 2.5rem !important;
    border: 1px solid #888 !important;
    border-radius: 4px !important;
    font-size: 1rem !important;
    padding-left: 48px !important;
  }

  .react-tel-input .flag-dropdown {
    border: none !important;
    background: transparent !important;
  }

  .react-tel-input .country-list {
    z-index: 9999 !important;
  }
`;

const FormWrapper = styled.div`
  background: #fff;
  width: 100%;
  max-width: 450px;
  border-radius: 16px;
  padding: 2rem 2.5rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  animation: fadeIn 0.4s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(15px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Title = styled.h2`
  text-align: center;
  color: #003644;
  font-weight: 600;
  margin-bottom: 1.5rem;
`;

const InputGroup = styled.div`
  position: relative;
  margin-bottom: 1.1rem;

  svg {
    position: absolute;
    top: 50%;
    left: 12px;
    transform: translateY(-50%);
    color: #777;
  }

  input {
    width: 100%;
    padding: 0.8rem 0.8rem 0.8rem 2.5rem;
    border: 1px solid #ccc;
    border-radius: 8px;
    font-size: 1rem;
    outline: none;
    transition: border 0.2s, box-shadow 0.2s;

    &:focus {
      border-color: #0096c7;
      box-shadow: 0 0 0 2px rgba(0, 150, 199, 0.2);
    }
  }
`;

const Botao = styled.button`
  width: 100%;
  background: #0096c7;
  color: white;
  font-weight: 600;
  font-size: 1rem;
  padding: 0.9rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-top: 0.6rem;
  transition: background 0.3s;

  &:hover {
    background: #00b4d8;
  }

  &:disabled {
    background: #90e0ef;
    cursor: not-allowed;
  }
`;

export default function ViewDadosPessoais() {
  const [formData, setFormData] = useState({
    nome: "",
    sobrenome: "",
    email: "",
    celular: "",
    cpf: "",
  });
  const [phone, setPhone] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { showSuccess, showError, showWarning, contextHolder } = useMessage();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.id]: e.target.value }));
  };

  const handlePhoneChange = (value: string) => {
    setPhone(value);
    setFormData((prev) => ({ ...prev, celular: numberPadrao(value) }));
  };

  // 🔹 Buscar dados do usuário logado
  useEffect(() => {
    const carregarDados = async () => {
      const Token = validateToken();
      if (!Token) return (document.location.href = "/inicio");

      try {
        const response = await axios.get(`${Globais.DOMINIOBACK}/usuario/${Token.id}`, {
          headers: { Authorization: Token.AuthStr },
        });

        if (response?.data) {
          const { nome, sobrenome, email, celular, cpf } = response.data;
          setFormData({ nome, sobrenome, email, celular, cpf });
          setPhone(celular);
        }
      } catch (err: any) {
        if (err.response?.status === 401) logout();
        else showError("Erro ao carregar seus dados. Tente novamente.");
      }
    };

    carregarDados();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const Token = validateToken();
    if (!Token) return (document.location.href = "/");

    if (!window.navigator.onLine)
      return showWarning("Sem conexão com a internet!");
    if (validateCelular(formData.celular))
      return showWarning(validateCelular(formData.celular));
    if (validateCPF(formData.cpf))
      return showWarning(validateCPF(formData.cpf));

    try {
      setIsLoading(true);

      const response = await axios.put(
        `${Globais.DOMINIOBACK}/usuario/${Token.id}`,
        formData,
        { headers: { Authorization: Token.AuthStr } }
      );

      if (response?.data) {
        showSuccess("✅ Dados atualizados com sucesso!");
        setTimeout(() => document.location.reload(), 1500);
      }
    } catch (err: any) {
      const code = err.response?.data?.code;
      const msg = err.response?.data?.error;
      switch (code) {
        case "EMAIL_DUPLICADO":
          showError("Já existe um cadastro com este e-mail.");
          break;
        case "CPF_DUPLICADO":
          showError("Já existe um cadastro com este CPF.");
          break;
        case "CELULAR_DUPLICADO":
          showError("Já existe um cadastro com este número de celular.");
          break;
        default:
          showError(msg || "❌ Ocorreu um erro ao atualizar os dados.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      {contextHolder}
      <FormWrapper>
        <form onSubmit={handleSubmit}>
          <Title>Meus dados pessoais</Title>

          <InputGroup>
            <User size={18} />
            <input
              type="text"
              id="nome"
              required
              placeholder="Nome"
              value={formData.nome}
              onChange={handleChange}
            />
          </InputGroup>

          <InputGroup>
            <User size={18} />
            <input
              type="text"
              id="sobrenome"
              required
              placeholder="Sobrenome"
              value={formData.sobrenome}
              onChange={handleChange}
            />
          </InputGroup>

          <InputGroup>
            <Mail size={18} />
            <input
              type="email"
              id="email"
              required
              placeholder="E-mail"
              value={formData.email}
              onChange={handleChange}
            />
          </InputGroup>

          <InputGroup>
            <Smartphone size={18} />
            <PhoneInput
              country={"br"}
              value={phone}
              onChange={handlePhoneChange}
              autoFormat={false}
              inputClass="phone-input"
              placeholder="Digite seu telefone"
            />
          </InputGroup>

          <InputGroup>
            <IdCard size={18} />
            <IMaskInput
              mask="000.000.000-00"
              id="cpf"
              placeholder="CPF"
              value={formData.cpf}
              onChange={handleChange}
              required
            />
          </InputGroup>

          <Botao type="submit" disabled={isLoading}>
            {isLoading ? <Spin size="small" /> : "Salvar Alterações"}
          </Botao>
        </form>
      </FormWrapper>
    </Container>
  );
}
